import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface CVAnalysisItem {
  id: number;
  role: string;
  number_of_cvs: number;
  status: 'draft' | 'completed';
  created_at: string;
}

interface CVAnalysesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CVAnalysisItem[];
}

interface UseCVAnalysesParams {
  search?: string;
}

/**
 * Hook for fetching CV analyses with search and pagination
 */
export const useCVAnalyses = ({ search }: UseCVAnalysesParams = {}) => {
  const { openToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cvAnalyses', search],
    queryFn: async () => {
      // This endpoint is server-paginated (DRF count/next/previous/results). The CV-analysis
      // table filters by status and paginates client-side, so it needs the full dataset — not
      // just the server's first page. Walk every page and accumulate the results. CV-analysis
      // jobs are a small per-account list, so this is at most a couple of requests.
      const PAGE_SIZE = 100;
      const MAX_PAGES = 100; // safety bound against an unexpected count/next loop
      const allResults: CVAnalysisItem[] = [];
      let page = 1;
      let totalCount = 0;

      while (page <= MAX_PAGES) {
        const params: Record<string, string | number> = {
          page,
          page_size: PAGE_SIZE,
        };
        if (search && search.trim()) {
          params.search = search.trim();
        }

        const response = await apiClient.get<CVAnalysesResponse>('/assessment/cv-analysis/', {
          params,
        });

        totalCount = response.data.count;
        allResults.push(...(response.data.results || []));

        if (!response.data.next || response.data.results.length === 0 || allResults.length >= totalCount) {
          break;
        }
        page += 1;
      }

      const fullData: CVAnalysesResponse = {
        count: totalCount,
        next: null,
        previous: null,
        results: allResults,
      };
      return fullData;
    },
    retry: 1,
  });

  const openToastRef = useRef(openToast);
  useEffect(() => {
    openToastRef.current = openToast;
  }, [openToast]);

  useEffect(() => {
    if (error) {
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch CV analyses';
      openToastRef.current({
        show: true,
        heading: 'Error',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    }
  }, [error]);

  return {
    cvAnalysesData: data,
    cvAnalyses: data?.results || [],
    totalCount: data?.count || 0,
    totalPages:
      data && Array.isArray(data.results) && data.results.length > 0
        ? Math.ceil((data.count || 0) / data.results.length)
        : 0,
    nextPage: data?.next,
    previousPage: data?.previous,
    isLoading,
    error,
    refetch,
  };
};